/**
 * CustomRangePicker — the dashboard's shared "Custom" range pill + inline
 * two-date react-datepicker. Used by the page-level DashboardDateRangeSelector
 * and by widgets with their own period filter (Aging Report), so the custom
 * date UX is identical everywhere on the dashboard.
 *
 * Selection is committed only once BOTH dates are picked (same date twice is
 * a valid single-day range); an in-progress first click stays local so the
 * popper doesn't close between clicks. On phones the calendar renders as a
 * centered portal overlay instead of an inline popper.
 */

import * as React from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangeSelector.css";

export interface CustomRangePickerProps {
  /** Committed range (YYYY-MM-DD) shown when `selected`; undefined = none yet. */
  fromDate?: string;
  toDate?: string;
  /** Whether the Custom pill is the active choice (drives label + tone). */
  selected: boolean;
  /** Called once both dates are picked, with YYYY-MM-DD strings. */
  onCommit: (fromDate: string, toDate: string) => void;
  /**
   * Class for the trigger pill. Callers pass their local pill style so the
   * trigger blends with the surrounding toggle (page header vs widget).
   */
  triggerClassName: string;
}

export function CustomRangePicker({
  fromDate,
  toDate,
  selected,
  onCommit,
  triggerClassName,
}: CustomRangePickerProps) {
  const isMobile = useIsMobile();

  const committedStart = parseYyyyMmDd(fromDate);
  const committedEnd = parseYyyyMmDd(toDate);

  // In-progress selection state — kept local so we don't commit (and
  // re-render the customInput, closing the popper) between clicks.
  const [pendingStart, setPendingStart] = React.useState<Date | null>(null);
  const pickerRef = React.useRef<DatePicker | null>(null);

  // Pending takes priority: once a new selection starts, ignore the
  // previously committed range so the calendar reflects the new attempt.
  const displayStart = pendingStart ?? committedStart;
  const displayEnd = pendingStart ? null : committedEnd;

  const label =
    selected && committedStart && committedEnd
      ? `${formatShort(committedStart)} – ${formatShort(committedEnd)}`
      : "Custom";

  const handleChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (start && end) {
      onCommit(formatYyyyMmDd(start), formatYyyyMmDd(end));
      setPendingStart(null);
      pickerRef.current?.setOpen(false);
    } else if (start) {
      setPendingStart(start);
    } else {
      setPendingStart(null);
    }
  };

  return (
    <DatePicker
      ref={pickerRef}
      selectsRange
      startDate={displayStart ?? undefined}
      endDate={displayEnd ?? undefined}
      // Reopen on the first day of the selected range (or the in-progress
      // pick) instead of always jumping to today's month.
      openToDate={displayStart ?? undefined}
      calendarClassName="dashboard-datepicker"
      onChange={handleChange}
      // Keep open after the first click; react-datepicker auto-closes once
      // both dates are picked AND onChange has committed.
      shouldCloseOnSelect={false}
      // Reset partial selection if the user clicks away without finishing.
      onCalendarClose={() => setPendingStart(null)}
      // No maxDate: future dates are allowed so aging can forecast "what
      // will this look like on date X" (and date-span widgets can look ahead).
      dateFormat="MM/dd/yyyy"
      withPortal={isMobile}
      popperPlacement="bottom-end"
      // Render the popper on document.body: widget cards create stacking
      // contexts (chart layers with their own z-index), so an inline popper
      // paints UNDER neighboring widgets no matter its own z-index.
      popperContainer={BodyPopperContainer}
      popperClassName="dashboard-datepicker-popper"
      // popper.js v1 (react-datepicker 3.x): shift the calendar back into the
      // viewport and flip when the preferred side doesn't fit — the trigger
      // pill can sit anywhere (page header right edge, widget left edge).
      popperModifiers={{
        preventOverflow: {
          enabled: true,
          escapeWithReference: false,
          boundariesElement: "viewport",
          padding: 8,
        },
        flip: { enabled: true, behavior: "flip" },
      }}
      customInput={
        <TriggerButton className={triggerClassName}>{label}</TriggerButton>
      }
    />
  );
}

/** Portals the calendar popper out of the widget's stacking context. */
function BodyPopperContainer({ children }: { children: React.ReactNode[] }) {
  return createPortal(children, document.body);
}

/** forwardRef trigger so react-datepicker can wire its customInput. */
const TriggerButton = React.forwardRef<
  HTMLButtonElement,
  {
    children?: React.ReactNode;
    className?: string;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  }
>(function TriggerButton({ children, className, onClick }, ref) {
  return (
    <button ref={ref} type="button" className={className} onClick={onClick}>
      {children}
    </button>
  );
});

/**
 * True when the viewport is at or below Tailwind's `sm` breakpoint (640px).
 * Switches the calendar to a centered portal overlay on phones.
 */
function useIsMobile(query = "(max-width: 640px)"): boolean {
  const getMatch = () =>
    typeof window !== "undefined" && window.matchMedia(query).matches;
  const [isMobile, setIsMobile] = React.useState<boolean>(getMatch);

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // addEventListener is the modern API; addListener the Safari < 14 fallback.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return isMobile;
}

function parseYyyyMmDd(s: string | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatShort(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}`;
}
